/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, OnInit, inject} from '@angular/core';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {
  ListBatteryObject,
  LoadListParams,
} from '../../../../models/ListBattery';
import {PassportService} from '../../../../services/passport.service';
import {ModalCreateBatteryPassComponent} from '../modal-create-battery-pass/modal-create-battery-pass.component';
import {AppSettings} from '../../../../models/Constants';
import {ModalCreateChildrenComponent} from '../modal-create-children/modal-create-children.component';
import {QrCodeModalComponent} from '../../../shared/modals/qr-code-modal/qr-code-modal.component';
import {RemovePassportModalComponent} from '../../../shared/modals/remove-passport-modal/remove-passport-modal.component';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WindowSizeService} from '../../../../services/window-size.service';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
  IRowNode,
  ITooltipParams,
  RowClassParams,
} from 'ag-grid-community';
import {getIxTheme} from '@siemens/ix-aggrid';
import * as agGrid from 'ag-grid-community';
import {
  SearchCriteria,
  SearchCriteriaStatus,
} from '../../../../models/SearchCriteria';
import {SchemaListItemDTO} from '../../../../models/Schema';
import {DropdownCellRendererComponent} from './dropdown-cell-renderer/dropdown-cell-renderer.component';
import {MasterChildStringEnum} from '../../../../models/Passport';
import {NgxRolesService} from 'ngx-permissions';
import {firstValueFrom} from 'rxjs';
import {getSchemaVersionLabel} from '../schema-version-label.pipe';

@Component({
  selector: 'app-list-battery-passes',
  templateUrl: './list-battery-passes.component.html',
  styleUrl: './list-battery-passes.component.scss',
  standalone: false,
})
export class ListBatteryPassesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly passportService = inject(PassportService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly rolesService = inject(NgxRolesService);

  protected readonly WindowSizeService = WindowSizeService;
  readonly schemaIdToNameMap: Map<string, string> = new Map();
  schemas: SchemaListItemDTO[] = [];

  loading = true;

  initialSearchCriteria: SearchCriteria;
  currentSearchCriteria: SearchCriteria | null = null;

  passListDetailsPaneExpanded = false;
  selectedPassport: ListBatteryObject;
  highlightedRow: IRowNode | null = null;

  totalElements = 0;
  columnDefs: ColDef[] = [];
  gridApi: GridApi | null = null;
  gridOptions: GridOptions = {
    theme: getIxTheme(agGrid),
    rowModelType: 'infinite',
    cacheBlockSize: AppSettings.BATTERY_LIST_PAGE_SIZE,
    maxBlocksInCache: 2,
    suppressRowClickSelection: true,
    rowSelection: 'single',
    pagination: false,
    enableCellTextSelection: true,
    suppressMovableColumns: true,
    suppressRowTransform: true,
    context: {
      componentParent: this,
      isDataManager: false,
    },
  };

  ngOnInit() {
    this.initialSearchCriteria = {...this.route.snapshot.queryParams};
    this.parsePassportStatus();
    const role = this.rolesService.getRole('DATA_MANAGER');
    this.gridOptions.context.isDataManager = role !== undefined;

    this.loadSchemas().then(() => {
      this.initializeGrid();
      this.loading = false;
    });

    this.translateService.onLangChange.subscribe(() => {
      if (this.gridApi) {
        this.gridApi.purgeInfiniteCache();
        this.gridApi.refreshHeader();
      }
    });
  }

  parsePassportStatus() {
    if (this.route.snapshot.queryParams['passportStatus']) {
      if (this.route.snapshot.queryParams['passportStatus'] === 'complete') {
        this.initialSearchCriteria.passportStatus = {
          done: true,
        } as SearchCriteriaStatus;
      } else if (
        this.route.snapshot.queryParams['passportStatus'] === 'draft'
      ) {
        this.initialSearchCriteria.passportStatus = {
          draft: true,
        } as SearchCriteriaStatus;
      } else {
        this.initialSearchCriteria.passportStatus = null;
      }
    }
  }

  private initializeGrid(): void {
    this.columnDefs = this.createColumnDefs();
  }

  async loadSchemas() {
    const schemas = await this.passportService.loadSchemaList();
    this.schemas = schemas.data;
    this.createSchemaMapping();
  }

  private createSchemaMapping(): void {
    this.schemaIdToNameMap.clear();
    this.schemas.forEach((schema) => {
      if (schema.id && schema.name) {
        this.schemaIdToNameMap.set(
          schema.id,
          `${schema.name} (${getSchemaVersionLabel(schema, this.schemas)})`,
        );
      }
    });
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.id_name'),
        flex: 2,
        minWidth: 150,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return passport?.metadata?.name || passport?.id;
          } else {
            return '<ix-spinner></ix-spinner>';
          }
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          return passport?.metadata?.name || passport?.id;
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.type'),
        minWidth: 150,
        maxWidth: 150,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (!passport) {
            return '';
          }
          let pillHtml;
          if (
            passport?.metadata.masterChildFlag === MasterChildStringEnum.MASTER
          ) {
            pillHtml = `<ix-pill variant="info">${this.translateService.instant('battery_pass.list.master')}</ix-pill>`;
          }
          if (
            passport?.metadata.masterChildFlag === MasterChildStringEnum.CHILD
          ) {
            pillHtml = `<ix-pill variant="neutral">${this.translateService.instant('battery_pass.list.child')}</ix-pill>`;
          }
          return `<div style="display: flex;" >${pillHtml}</div>`;
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.product'),
        flex: 1.5,
        minWidth: 150,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (!passport) {
            return '';
          }
          const schemaId = passport.metadata?.schemaId;
          return this.getSchemaNameById(schemaId);
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          if (!passport) {
            return '';
          }
          const schemaId = passport.metadata?.schemaId;
          return this.getSchemaNameById(schemaId);
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.created_at'),
        maxWidth: 180,
        minWidth: 180,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return new Date(passport?.metadata?.createdAt).toLocaleString();
          } else {
            return '';
          }
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return new Date(passport?.metadata?.createdAt).toLocaleString();
          } else {
            return '';
          }
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.modified_at'),
        maxWidth: 180,
        minWidth: 180,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return new Date(passport?.metadata?.updatedAt).toLocaleString();
          } else {
            return '';
          }
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return new Date(passport?.metadata?.updatedAt).toLocaleString();
          } else {
            return '';
          }
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.status'),
        minWidth: 125,
        maxWidth: 125,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (!passport) {
            return '';
          }
          const status = passport.metadata.status.status;
          if (!status) {
            return '';
          }

          const success = status === 'draft' ? 'critical' : 'success';
          const translatedStatus = this.translateService.instant(
            `battery_pass.list.${status}`,
          );

          return `<div style="display: flex;" ><ix-pill variant="${success}">${translatedStatus}</ix-pill></div>`;
        },
      },
      {
        headerName: '',
        field: 'actions',
        minWidth: 64,
        maxWidth: 64,
        cellRenderer: DropdownCellRendererComponent,
        cellClass: 'dropdown-cell',
        cellEditor: 'agSelectCellEditor',
        editable: true,
        cellEditorPopup: true,
      },
    ];
  }

  getSchemaNameById(schemaId: string | undefined): string | undefined {
    return schemaId ? this.schemaIdToNameMap.get(schemaId) : undefined;
  }

  async onSearchCriteria(criteria: SearchCriteria): Promise<void> {
    this.currentSearchCriteria = criteria;
    await this.refreshGridWithSearch(criteria);
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    await this.loadList(this.initialSearchCriteria);
  }

  async loadList(searchCriteria?: SearchCriteria) {
    if (this.gridApi) {
      const datasource: IDatasource = {
        getRows: (params: IGetRowsParams) => {
          const loadListParams: LoadListParams = {
            limit: params.endRow - params.startRow,
            offset: params.startRow,
            passportId: searchCriteria?.passportId ?? undefined,
            passportName: searchCriteria?.passportName ?? undefined,
            type: searchCriteria?.type ?? undefined,
            passportStatus: searchCriteria?.passportStatus ?? undefined,
            schemaId: searchCriteria?.schemaId ?? undefined,
            createdAtFrom: searchCriteria?.createdAtFrom ?? undefined,
            createdAtTo: searchCriteria?.createdAtTo ?? undefined,
            updatedAtFrom: searchCriteria?.updatedAtFrom ?? undefined,
            updatedAtTo: searchCriteria?.updatedAtTo ?? undefined,
          };
          this.passportService
            .loadList(loadListParams)
            .then((result) => {
              this.totalElements = result.meta?.page.totalElements ?? 0;
              params.successCallback(
                result.data,
                result.meta?.page.totalElements,
              );
            })
            .catch(() => {
              this.totalElements = 0;
              params.failCallback();
            });
        },
      };

      this.gridApi.setGridOption('datasource', datasource);
    }
  }

  private async refreshGridWithSearch(criteria: SearchCriteria): Promise<void> {
    if (this.gridApi) {
      await this.loadList(criteria);
    }
  }

  onCellClicked(event: CellClickedEvent) {
    // Update old and new highlighted rows
    const oldRow = this.highlightedRow;
    this.highlightedRow = event.node;

    const rows = [this.highlightedRow];
    if (oldRow) {
      rows.push(oldRow);
    }

    event.api.redrawRows({rowNodes: rows});
    setTimeout(() => {
      this.passListDetailsPaneExpanded = true;
      this.selectedPassport = event.data;
    }, 0);
  }

  onPassListDetailPaneChanged(event: CustomEvent) {
    setTimeout(() => {
      this.passListDetailsPaneExpanded = event.detail.expanded;
    }, 0);
  }

  async onDetailEventClick() {
    this.gridApi?.purgeInfiniteCache();
  }

  async handleActionClick(action: string, id: string) {
    const passport = this.findPassportById(id);

    if (!passport) {
      return;
    }

    switch (action) {
      case 'qr-code':
        await this.onShowQrClick(passport);
        break;
      case 'addIndividual':
        await this.addNewIndividualPassportClick(passport);
        break;
      case 'delete':
        await this.onRemovePassportClick(passport);
        break;
      default:
        break;
    }
  }

  private findPassportById(id: string): ListBatteryObject | null {
    if (!this.gridApi) {
      return null;
    }

    let foundPassport: ListBatteryObject | null = null;

    this.gridApi.forEachNode((node) => {
      if (node.data?.id === id) {
        foundPassport = node.data;
      }
    });

    return foundPassport;
  }

  async addNewPassportClick() {
    await this.modalService.open({
      size: '840',
      content: ModalCreateBatteryPassComponent,
      centered: true,
      closeOnBackdropClick: true,
    });
  }

  async addNewIndividualPassportClick(passport: ListBatteryObject) {
    const masterPassportId = passport.id;
    const instance = await this.modalService.open({
      size: '480',
      content: ModalCreateChildrenComponent,
      data: {id: masterPassportId},
      centered: true,
      closeOnBackdropClick: true,
    });

    instance.onClose.on(async (finishText: string) => {
      if (finishText === 'finish') {
        this.gridApi?.purgeInfiniteCache();
      }
    });
  }

  async onShowQrClick(passport: ListBatteryObject) {
    const id = passport.id;
    if (!id) {
      return;
    }

    await this.modalService.open({
      size: '360',
      content: QrCodeModalComponent,
      data: {
        width: 240,
        data: passport.metadata?.qrCodeContent,
        margin: 3,
      },
      centered: true,
    });
  }

  async onRemovePassportClick(passport: ListBatteryObject) {
    const id = passport.id;
    if (!id) {
      return;
    }

    const name = passport.metadata.name;
    const instance = await this.modalService.open({
      content: RemovePassportModalComponent,
      centered: true,
      data: {
        passport: passport,
        name: name ?? id,
      },
    });

    instance.onClose.on(async (shouldRemove: boolean) => {
      if (shouldRemove) {
        await firstValueFrom(this.passportService.delete$(id));
        this.passListDetailsPaneExpanded = false;
        this.gridApi?.purgeInfiniteCache();
        this.translateService
          .get('battery_pass.list.removing_battery_pass_success')
          .subscribe((msg: string) => {
            this.toastService.show({message: msg, type: 'success'});
          });
      }
    });
  }

  getRowClass(params: RowClassParams): string | undefined {
    if (params.rowIndex === this.highlightedRow?.rowIndex) {
      return 'highlighted-row';
    }

    return undefined;
  }
}
